const prefersReduced = (): boolean =>
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function mountParticleNetwork(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};

  const COLORS = ['#007aff', '#5ac8fa', '#5e5ce6'];
  let raf = 0;
  let w = 0;
  let h = 0;
  let dpr = Math.min(window.devicePixelRatio || 1, 2);
  const mouse = { x: -9999, y: -9999 };

  type P = { x: number; y: number; vx: number; vy: number; r: number; c: string };
  let pts: P[] = [];

  const seed = () => {
    const count = Math.min(64, Math.floor((w * h) / 16000));
    pts = Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      r: Math.random() * 1.8 + 0.8,
      c: COLORS[(Math.random() * COLORS.length) | 0],
    }));
  };

  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    w = rect.width;
    h = rect.height;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    seed();
  };

  const tick = () => {
    ctx.clearRect(0, 0, w, h);
    for (const p of pts) {
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < 0 || p.x > w) p.vx *= -1;
      if (p.y < 0 || p.y > h) p.vy *= -1;

      const dx = mouse.x - p.x;
      const dy = mouse.y - p.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < 18000) {
        p.x += dx * 0.0009;
        p.y += dy * 0.0009;
      }
    }

    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const a = pts[i];
        const b = pts[j];
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const dist = Math.hypot(dx, dy);
        if (dist < 116) {
          const o = (1 - dist / 116) * 0.5;
          ctx.strokeStyle = `rgba(0,122,255,${o.toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
    }

    for (const p of pts) {
      const near = Math.hypot(mouse.x - p.x, mouse.y - p.y) < 130;
      ctx.beginPath();
      ctx.fillStyle = p.c;
      ctx.globalAlpha = near ? 0.95 : 0.6;
      ctx.arc(p.x, p.y, near ? p.r * 1.6 : p.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(tick);
  };

  const onMove = (e: PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = e.clientX - rect.left;
    mouse.y = e.clientY - rect.top;
  };
  const onLeave = () => {
    mouse.x = -9999;
    mouse.y = -9999;
  };

  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(canvas);
  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('pointerout', onLeave, { passive: true });

  if (!prefersReduced()) {
    raf = requestAnimationFrame(tick);
  } else {
    tick();
    cancelAnimationFrame(raf);
  }

  return () => {
    cancelAnimationFrame(raf);
    ro.disconnect();
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerout', onLeave);
  };
}

export function mountParallax(scope: HTMLElement): void {
  if (prefersReduced()) return;
  const layers = Array.from(scope.querySelectorAll<HTMLElement>('[data-parallax]'));
  if (!layers.length) return;
  let rx = 0;
  let ry = 0;
  let tx = 0;
  let ty = 0;
  let raf = 0;

  const loop = () => {
    rx += (tx - rx) * 0.08;
    ry += (ty - ry) * 0.08;
    for (const el of layers) {
      const depth = Number(el.dataset.parallax || '10');
      el.style.setProperty('--px', `${(rx * depth).toFixed(2)}px`);
      el.style.setProperty('--py', `${(ry * depth).toFixed(2)}px`);
    }
    raf = requestAnimationFrame(loop);
  };

  scope.addEventListener(
    'pointermove',
    (e) => {
      const rect = scope.getBoundingClientRect();
      tx = (e.clientX - rect.left) / rect.width - 0.5;
      ty = (e.clientY - rect.top) / rect.height - 0.5;
    },
    { passive: true },
  );
  scope.addEventListener('pointerleave', () => {
    tx = 0;
    ty = 0;
  });
  raf = requestAnimationFrame(loop);
  void raf;
}

export function mountCounters(): void {
  const els = Array.from(document.querySelectorAll<HTMLElement>('[data-count]'));
  if (!els.length) return;
  const run = (el: HTMLElement) => {
    const target = Number(el.dataset.count || '0');
    const suffix = el.dataset.suffix || '';
    const dur = 1400;
    if (prefersReduced()) {
      el.textContent = formatNum(target) + suffix;
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = formatNum(Math.round(target * eased)) + suffix;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          run(e.target as HTMLElement);
          io.unobserve(e.target);
        }
      }
    },
    { threshold: 0.4 },
  );
  els.forEach((el) => io.observe(el));
}

function formatNum(n: number): string {
  return n >= 1000 ? n.toLocaleString('en-US') : String(n);
}

export function mountCardSpotlight(selector: string): void {
  const cards = Array.from(document.querySelectorAll<HTMLElement>(selector));
  const reduced = prefersReduced();
  for (const card of cards) {
    card.addEventListener(
      'pointermove',
      (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        card.style.setProperty('--mx', `${x}px`);
        card.style.setProperty('--my', `${y}px`);
        if (!reduced) {
          const rx = ((y / rect.height) - 0.5) * -5;
          const ry = ((x / rect.width) - 0.5) * 5;
          card.style.setProperty('--rx', `${rx.toFixed(2)}deg`);
          card.style.setProperty('--ry', `${ry.toFixed(2)}deg`);
        }
      },
      { passive: true },
    );
    card.addEventListener('pointerleave', () => {
      card.style.setProperty('--rx', '0deg');
      card.style.setProperty('--ry', '0deg');
    });
  }
}

export function mountScrollProgress(bar: HTMLElement): void {
  const onScroll = () => {
    const h = document.documentElement;
    const max = h.scrollHeight - h.clientHeight;
    const p = max > 0 ? h.scrollTop / max : 0;
    bar.style.transform = `scaleX(${p.toFixed(4)})`;
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
}

export function mountGoSnapWorkflow(): () => void {
  const root = document.querySelector('.gs-workflow');
  if (!root) return () => {};

  const steps = root.querySelectorAll<HTMLElement>('.gs-workflow__step');
  const slides = root.querySelectorAll<HTMLElement>('.gs-workflow__slide');
  if (!steps.length || !slides.length) return () => {};

  const setActive = (index: number) => {
    steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
  };

  if (prefersReduced()) {
    setActive(0);
    steps.forEach((step) => step.classList.add('is-active'));
    return () => {};
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = Number((entry.target as HTMLElement).dataset.step);
        if (!Number.isNaN(idx)) setActive(idx);
      });
    },
    { rootMargin: '-42% 0px -42% 0px', threshold: 0 },
  );

  steps.forEach((step) => io.observe(step));
  setActive(0);

  return () => io.disconnect();
}

export function mountUniteBasePillars(): () => void {
  const root = document.querySelector('.ub-pillars__layout');
  if (!root) return () => {};

  const pillars = root.querySelectorAll<HTMLElement>('.ub-pillar');
  const lineFill = root.querySelector<HTMLElement>('.ub-pillars__line-fill');
  if (!pillars.length) return () => {};

  const setActive = (index: number) => {
    pillars.forEach((p, i) => p.classList.toggle('is-active', i === index));
    if (lineFill) {
      const progress = ((index + 1) / pillars.length) * 100;
      lineFill.style.setProperty('--ub-pipe-progress', `${progress}%`);
      lineFill.style.height = `${progress}%`;
    }
  };

  if (prefersReduced()) {
    pillars.forEach((p) => p.classList.add('is-active'));
    if (lineFill) lineFill.style.height = '100%';
    return () => {};
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = Number((entry.target as HTMLElement).dataset.pillar);
        if (!Number.isNaN(idx)) setActive(idx);
      });
    },
    { rootMargin: '-35% 0px -35% 0px', threshold: 0 },
  );

  pillars.forEach((p) => io.observe(p));
  setActive(0);

  return () => io.disconnect();
}
